/**
 * Construct proper image URL for storage images
 * @param {string} imagePath - Image path from database
 * @param {string} fallback - Fallback image path
 * @returns {string} - Properly constructed image URL
 */
export const getImageUrl = (imagePath, fallback = "/default-room.jpg") => {
  if (!imagePath) return fallback;

  // If an array is passed, recurse on the first element
  if (Array.isArray(imagePath)) {
    return imagePath.length > 0 ? getImageUrl(imagePath[0], fallback) : fallback;
  }

  // Ensure it's a string before calling string methods
  if (typeof imagePath !== "string") return fallback;

  // If it's already a full URL, return as-is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it starts with /images/ (static assets), return as-is
  if (imagePath.startsWith("/images/")) {
    return imagePath;
  }

  let backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8000";
  let finalUrl = "";
  if (imagePath.startsWith("storage/") || imagePath.startsWith("/storage/")) {
    // Ensure exactly one slash between backendUrl and imagePath
    finalUrl = backendUrl.replace(/\/?$/, "/") + imagePath.replace(/^\//, "");
    return finalUrl;
  }

  // For any other relative path, assume it needs the backend URL
  finalUrl = backendUrl.replace(/\/?$/, "/") + imagePath.replace(/^\//, "");
  return finalUrl;
};

/**
 * Get the first available image from an array of images
 * @param {Array} images - Array of image paths
 * @param {string} fallback - Fallback image path
 * @returns {string} - Properly constructed image URL
 */
export const getFirstImageUrl = (images, fallback = "/default-room.jpg") => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return fallback;
  }
  
  return getImageUrl(images[0], fallback);
};

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width (default: 1200)
 * @param {number} maxHeight - Maximum height (default: 800) 
 * @param {number} quality - Compression quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Promise resolving to compressed file
 */
export const compressImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    const outputType = file.type || 'image/jpeg';

    const cleanup = () => URL.revokeObjectURL(objectUrl);

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio. A single
      // scale factor (rather than branching on orientation) is required so
      // that both maxWidth AND maxHeight are respected together — e.g. a
      // 1100x900 image is landscape but still taller than an 800px cap.
      const { width, height } = img;
      const scale = Math.min(1, maxWidth / width, maxHeight / height);
      const targetWidth = Math.round(width * scale);
      const targetHeight = Math.round(height * scale);

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      canvas.toBlob(
        (blob) => {
          cleanup();
          if (!blob) {
            reject(new Error(`Failed to compress image: ${file.name}`));
            return;
          }
          const compressedFile = new File([blob], file.name, {
            type: outputType,
            lastModified: Date.now()
          });
          resolve(compressedFile);
        },
        outputType,
        quality
      );
    };

    img.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load image: ${file.name}`));
    };

    img.src = objectUrl;
  });
};

/**
 * Compress multiple image files
 * @param {FileList|Array} files - The image files to compress
 * @param {number} maxWidth - Maximum width (default: 1200)
 * @param {number} maxHeight - Maximum height (default: 800)
 * @param {number} quality - Compression quality 0-1 (default: 0.8)
 * @returns {Promise<Array<File>>} - Promise resolving to array of compressed files
 */
export const compressImages = async (files, maxWidth = 1200, maxHeight = 800, quality = 0.8) => {
  const fileArray = Array.from(files);
  const compressPromises = fileArray.map(file => 
    file.type.startsWith('image/') 
      ? compressImage(file, maxWidth, maxHeight, quality)
      : Promise.resolve(file) // Return non-image files as-is
  );
  
  return Promise.all(compressPromises);
};